import * as React from 'react';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';
import { TabBar } from '@longline/aqua-ui/controls/TabBar';

import { SystemForm } from '../SystemForm';
import { TTab, useSystemsContext } from '../ISystemsContext';
import { Pane } from '../../../ui/Pane';
import { ISystem, ISystemLayer } from '../../../api/models/ISystem';
import { ExcelImport } from '../../../dialogs/ExcelImport';
import { ILayer } from '../../../types/ILayer';
import { ELayerType } from '../../../types/ELayerType';
import { LayerManager } from '../LayerManager';
import { DriversetManager } from '../DriversetManager';
import { IDriverset } from '../../../api/models';

const AddSystem = () => {
  const ctx = useSystemsContext();
  const toastProvider = useToast();

  const systemRef = React.useRef<ISystem>({ active: true, layers: [], driversets: [] } as unknown as ISystem);
  const [system, setSystem] = React.useState<ISystem>(systemRef.current);

  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState<TTab>('info');
  const [importing, setImporting] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  const controller = React.useRef<AbortController | null>(null);

  const handleChange = (value: ISystem, forceupdate: boolean) => {
    // If country changes, then division must reset to null.
    if(Object.keys(value).includes('country') && systemRef.current.country != value.country) {
      value.division = undefined;
    }
    // Only assign to local ref, so form doesn't update.
    Object.assign(systemRef.current, value);
    // ... unless forceupdate, then update the local state so that the form
    // *does* update.
    if(forceupdate) {
      setSystem({...systemRef.current});
    }
  }

  const handleImportLayer = (layers: ILayer[]) => {
    setImporting(false);
    layers.forEach(layer => {
      systemRef.current.layers.push({ name: layer.source, type: layer.type, data: layer.data });
    });
  } 

  const handleEditLayer = (layer: ISystemLayer, name: string) => {
    layer.name = name;
    setSystem({ ...systemRef.current });
  }

  const handleChangeDriversets = (driversets: IDriverset[]) => {
    systemRef.current.driversets = driversets;
    setSystem({ ...systemRef.current });
  }

  const handleDeleteLayer = (layer: ISystemLayer) => {
    systemRef.current.layers = systemRef.current.layers.filter(l => l != layer);
    setSystem({ ...systemRef.current });
  }  

  const handleSubmit = async () => {
    if(loading) return;

    if(!isValid) {
      setDirty(true);
      setTab('info');
      return;
    }

    const bathymetryCount = systemRef.current.layers.filter(f => f.type == ELayerType.Depth).length;
    if(bathymetryCount == 0) {
      toastProvider.toast("Please upload a bathymetry dataset.", { dismissable: true });
      setTab('datasets');
      return;
    }    
    if(bathymetryCount > 1) {
      toastProvider.toast("A system cannot have more than one bathymetry dataset.", { dismissable: true });
      setTab('datasets');
      return;
    }        
    if(systemRef.current.layers.filter(f => f.type == ELayerType.CurrentSpeed).length < 1) {
      toastProvider.toast("Please upload at least one current speed dataset.", { dismissable: true });
      setTab('datasets');
      return;
    }
    if(systemRef.current.driversets.length < 1) {
      toastProvider.toast("Please create at least one driver set.", { dismissable: true });
      setTab('driversets');
      return;
    }

    setLoading(true);
    controller.current = new AbortController();
    await ctx.createSystem(systemRef.current, controller.current.signal);
    setLoading(false);
  }

  React.useEffect(() => {
    return () => {
      if(controller.current) controller.current.abort();
    }
  }, []);

  return (
    <>
      <Pane.Heading>
        <Pane.Title>Systems</Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable>
        Add system
      </Pane.Subheader>

      <TabBar active={tab === 'datasets' ? 1 : (tab === 'driversets' ? 2 : 0)} line>
        <TabBar.Tab onClick={() => setTab('info')}>System info</TabBar.Tab>
        <TabBar.Tab onClick={() => setTab('datasets')} badge={system.layers.length}>Datasets</TabBar.Tab>
        <TabBar.Tab onClick={() => setTab('driversets')} badge={(system.driversets || []).length}>Driver sets</TabBar.Tab>
      </TabBar>

      <div style={{display: tab === 'info' ? 'block' : 'none', flex: 1 }}>
        <Pane.Body>
          <SystemForm
            dirty={dirty}
            system={system}
            onChange={handleChange}
            onValidate={(v: boolean) => setIsValid(v)}
            disabled={loading}
          />
        </Pane.Body>
      </div>

      <div style={{display: tab === 'datasets' ? 'block' : 'none', flex: 1 }}>
        <LayerManager layers={[...system.layers]} onAdd={() => setImporting(true)} onDelete={handleDeleteLayer} onEdit={handleEditLayer}/>
      </div>

      <div style={{display: tab === 'driversets' ? 'block' : 'none', flex: 1 }}>
        <DriversetManager driversets={system.driversets || []} onChange={handleChangeDriversets}/>
      </div>

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('list')}/>
        <Pane.SubmitButton onClick={() => handleSubmit()} working={loading} disabled={loading}/>
      </Pane.Footer>

      {importing && <ExcelImport columns={['latitude', 'longitude', 'depth', 'u', 'v']} title="Import data from Excel" onCancel={() => setImporting(false)} onImport={handleImportLayer} />}
    </>
  )
}

export { AddSystem }
